import { ComponentType, lazy } from 'react'

/**
 * Drop-in replacement for React.lazy that recovers from a failed dynamic import.
 *
 * After a deploy, the old content-hashed chunk no longer exists on the server.
 * The Firebase SPA rewrite then serves index.html (HTML) in its place, which the
 * browser can't parse as JavaScript — the "Uncaught SyntaxError: Unexpected token '<'"
 * / ChunkLoadError users see when navigating with a tab that was open across a deploy.
 *
 * On the first such failure we reload once to pull the fresh index.html and the
 * current chunk hashes. A sessionStorage flag prevents reload loops and is cleared
 * on the next successful load, so a later deploy can recover the same way.
 */
const RELOAD_FLAG = 'sb_chunk_reload'

function hasReloaded(): boolean {
    try {
        return window.sessionStorage.getItem(RELOAD_FLAG) === '1'
    } catch {
        // sessionStorage can be unavailable (private mode, blocked storage) — fail open.
        return false
    }
}

function setReloaded(value: boolean): void {
    try {
        if (value) {
            window.sessionStorage.setItem(RELOAD_FLAG, '1')
        } else {
            window.sessionStorage.removeItem(RELOAD_FLAG)
        }
    } catch {
        /* ignore storage errors */
    }
}

export function lazyWithRetry<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
    return lazy(async () => {
        try {
            const component = await factory()
            // Loaded fine — reset the guard so a future deploy can trigger a reload again.
            setReloaded(false)
            return component
        } catch (error) {
            // Chunk missing — almost always a stale deploy. Reload once to fetch fresh assets.
            if (!hasReloaded()) {
                setReloaded(true)
                // eslint-disable-next-line no-console
                console.warn('[lazyWithRetry] chunk failed to load — reloading once to fetch the fresh build', error)
                window.location.reload()
                // Keep React in the Suspense fallback until the reload takes over.
                return new Promise<{ default: T }>(() => undefined)
            }
            // Already retried once and still failing — let RouteErrorBoundary handle it.
            throw error
        }
    })
}
